import { useCallback } from "react";

export function useCalibrationPanelController({
  streamRef,
  startCamera,
  setShowPuzzle,
  isActive,
  isFirstTimeVideo,
  videoCalibrationDurationRef,
  setShowVideoOverlay,
  setHoldOverlayUntilVideoEnd,
  setShowUnmuteCTA,
  setVideoAudioBlocked,
  setCalibrating,
  setIsCalibrationComplete,
  setCalibrationProgress,
  configuredCalibrationDurationMs,
  start,
  sessionRef,
  getCalibrationScreenAnchor,
  introVideoRef,
  recordEvent,
  setIsVideoPlaying,
  setIsFirstTimeVideo,
  stopIntroPlayback,
  cameraAvailable,
  productionFlowStarted,
  productionPrivacyAccepted,
  setError,
  setProductionFlowStarted,
}) {
  const defaultCalibrationDurationMs = Number.isFinite(configuredCalibrationDurationMs)
    ? configuredCalibrationDurationMs
    : 1200;

  const openCalibrationPanel = useCallback(
    async (forcePlay = false) => {
      try {
        if (!streamRef.current) await startCamera();
        setShowPuzzle(true);
        const shouldStartSession = !isActive;

        if (!isFirstTimeVideo && !forcePlay) {
          videoCalibrationDurationRef.current = null;
          setShowVideoOverlay(false);
          setHoldOverlayUntilVideoEnd(false);
          setShowUnmuteCTA(false);
          setVideoAudioBlocked(false);
          setCalibrating(true);
          setIsCalibrationComplete(false);
          setCalibrationProgress({ waitingForFace: true, sampleCount: 0, elapsedMs: 0, durationMs: defaultCalibrationDurationMs, minSamples: 12 });

          if (shouldStartSession) {
            await start(defaultCalibrationDurationMs);
          }

          window.requestAnimationFrame(() => {
            sessionRef.current?.startLocalCalibration?.(getCalibrationScreenAnchor());
          });
          return;
        }

        setShowVideoOverlay(true);
        setHoldOverlayUntilVideoEnd(!!forcePlay);
        setShowUnmuteCTA(false);
        setVideoAudioBlocked(false);
        setIsCalibrationComplete(false);

        await new Promise((resolve) => {
          let tries = 0;
          const tick = () => {
            if (introVideoRef.current) return resolve();
            tries += 1;
            if (tries > 40) return resolve();
            window.setTimeout(tick, 50);
          };
          tick();
        });

        const videoEl = introVideoRef.current;

        await new Promise((resolve) => {
          if (!videoEl) return resolve();
          if (videoEl.readyState >= 1 && videoEl.duration && !isNaN(videoEl.duration)) return resolve();
          const onLoaded = () => resolve();
          videoEl.addEventListener("loadedmetadata", onLoaded, { once: true });
          setTimeout(resolve, 1500);
        });

        const duration = introVideoRef.current?.duration ?? null;
        const videoDurationMs = duration && !isNaN(duration) ? Math.round(duration * 1000) : null;
        const calibrationMs = defaultCalibrationDurationMs;
        if (calibrationMs) videoCalibrationDurationRef.current = calibrationMs;
        setCalibrationProgress({ waitingForFace: true, sampleCount: 0, elapsedMs: 0, durationMs: calibrationMs, minSamples: 12 });

        if (shouldStartSession) {
          await start(calibrationMs);
        }

        let played = false;
        if (videoEl) {
          try {
            videoEl.muted = false;
            await videoEl.play();
            played = true;
            setVideoAudioBlocked(false);
            setShowUnmuteCTA(false);
            recordEvent("video_audio_allowed");
          } catch {
            try {
              videoEl.muted = true;
              await videoEl.play();
              played = true;
              setVideoAudioBlocked(true);
              setShowUnmuteCTA(true);
              recordEvent("video_muted_fallback");
            } catch {
              recordEvent("video_play_failed");
            }
          }
        }

        if (played) {
          setIsVideoPlaying(true);
          recordEvent("video_started");
        }

        if (played) {
          window.requestAnimationFrame(() => {
            sessionRef.current?.startLocalCalibration?.(getCalibrationScreenAnchor());
          });
        }

        const onEnded = () => {
          recordEvent("video_completed");
          try {
            if (typeof localStorage !== "undefined") localStorage.setItem("eie:videoWatchedAt", String(Date.now()));
          } catch {
            // ignore persistence failures
          }
          setIsFirstTimeVideo(false);
          stopIntroPlayback(true);
          if (forcePlay) {
            setHoldOverlayUntilVideoEnd(false);
            setShowVideoOverlay(false);
            setShowPuzzle(false);
          }
        };

        if (introVideoRef.current) introVideoRef.current.onended = onEnded;
      } catch (err) {
        console.warn("openCalibrationPanel failed", err);
        try {
          await start(defaultCalibrationDurationMs);
          window.requestAnimationFrame(() => {
            sessionRef.current?.startLocalCalibration?.(getCalibrationScreenAnchor());
          });
        } catch (e) {
          console.warn("start failed", e);
        }
      }
    },
    [
      streamRef,
      startCamera,
      setShowPuzzle,
      isActive,
      isFirstTimeVideo,
      videoCalibrationDurationRef,
      setShowVideoOverlay,
      setHoldOverlayUntilVideoEnd,
      setShowUnmuteCTA,
      setVideoAudioBlocked,
      setCalibrating,
      setIsCalibrationComplete,
      setCalibrationProgress,
      defaultCalibrationDurationMs,
      start,
      sessionRef,
      getCalibrationScreenAnchor,
      introVideoRef,
      recordEvent,
      setIsVideoPlaying,
      setIsFirstTimeVideo,
      stopIntroPlayback,
    ]
  );

  const startProductionExperience = useCallback(async () => {
    if (isActive || productionFlowStarted) return;
    if (!cameraAvailable) {
      setError("Camera not available");
      return;
    }
    try {
      setProductionFlowStarted(true);
      setShowPuzzle(true);
      setError(null);
      if (productionPrivacyAccepted) {
        await openCalibrationPanel();
      }
      return;
    } catch (e) {
      setError(e?.message || "Unable to start EIE experience");
      setCalibrating(false);
      setProductionFlowStarted(false);
    }
  }, [
    isActive,
    productionFlowStarted,
    cameraAvailable,
    setError,
    setProductionFlowStarted,
    setShowPuzzle,
    productionPrivacyAccepted,
    openCalibrationPanel,
    setCalibrating,
  ]);

  return {
    openCalibrationPanel,
    startProductionExperience,
  };
}
